/**
 * IMPORTS
 */
import * as commands from 'src/aggregates/conversations/commands';
import * as handlers from 'src/aggregates/conversations/handlers';
import {execute} from 'src/aggregates/execute';


/**
 * TYPES
 */
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {ITemplateInput} from 'src/aggregates/conversations/queries.d';
import {IMessage} from 'src/aggregates/conversations/state.d';
import {IMetricsFilters} from 'src/aggregates/conversations/state.d';
import {IQualityMetadata} from 'src/aggregates/conversations/state.d';
import {IFeedbackParams} from 'src/aggregates/utils/feedback';
import {ToastTypes} from 'src/aggregates/utils/feedback';
import {IMessage as IComponentMessage}
    from 'src/components/Shared/Message/index.d';


/**
 * CODE
 */

/**
 * I am an API to add forwarding message.
 *
 * :param message: message to forward
 *
 * :returns: promise with nothing
 */
async function addForwardingMessage (
    message: IComponentMessage,
): Promise<void>
{
    await execute(
        commands.addForwardingMessage(message),
        handlers.addForwardingMessage,
    );
}


/**
 * I am an API to listen conversations endpoints.
 *
 * :returns: promise with nothing
 */
async function listen (): Promise<void>
{
    await execute(commands.listen(), handlers.listen);
}


/**
 * I am an API to remove a conversation.
 *
 * :param id: conversation id
 *
 * :returns: promise with nothing
 */
async function remove (id: number): Promise<void>
{
    await execute(commands.remove(id), handlers.remove);
}


/**
 * I am an API to transfer a conversation.
 *
 * :param id: conversation id
 * :param to: receiver agent uid
 *
 * :returns: promise with nothing
 */
async function transfer (id: number, to: string): Promise<void>
{
    await execute(commands.transfer(id, to), handlers.transfer);
}


/**
 * I am an API to close session on conversis.
 *
 * :returns: promise with nothing
 */
async function closeSession (): Promise<void>
{
    await execute(commands.closeSession(), handlers.closeSession);
}


/**
 * I am an api to create a conversation by the agent.
 *
 * :param account: account id
 * :param contact: contact value
 * :param subject: conversation subject
 * :param type: account type
 *
 * :returns: promise with nothing
 */
async function create (
    account: number,
    contact: string,
    subject: string | null,
    type: number,
): Promise<void>
{
    await execute(commands.create(account, contact, subject, type),
                  handlers.create);
}

/**
 * I am an API to ensure the conversation is on store.
 *
 * :param conversation: conversation id
 *
 * :returns: promise with nothing
 */
async function ensure (conversation: number): Promise<void>
{
    await execute(commands.ensure(conversation), handlers.ensure);
}


/**
 * I am an API to ensure the ongoing conversation is on store.
 *
 * :param conversation: conversation id
 *
 * :returns: promise with nothing
 */
async function ensureOngoing (conversation: number): Promise<void>
{
    await execute(
        commands.ensureOngoing(conversation),
        handlers.ensureOngoing,
    );
}


/**
 * I am an api to fetch a conversation.
 *
 * :param id: conversation id
 *
 * :returns: promise with nothing
 */
async function fetch (id: number): Promise<void>
{
    await execute(commands.fetch(id), handlers.fetch);
}


/**
 * I am an API to fetch agents messages metrics.
 *
 * :param filters: metrics filters
 *
 * :returns: promise with nothing
 */
async function fetchAgentsMessagesMetrics (
    filters?: IMetricsFilters,
): Promise<void>
{
    await execute(
        commands.fetchAgentsMessagesMetrics(filters),
        handlers.fetchAgentsMessagesMetrics,
    );
}


/**
 * I am an api to fetch conversations by ids.
 *
 * :param conversations: list of conversation ids
 *
 * :returns: promise with nothing
 */
async function fetchByIds (conversations: number[]): Promise<void>
{
    await execute(
        commands.fetchByIds(conversations),
        handlers.fetchByIds,
    );
}


/**
 * I am an API to fetch a message by original id.
 *
 * :param message: message id
 * :param reply: original id of message being replied
 * :param conversation: conversation id of replying message
 *
 * :returns: promise with nothing
 */
async function fetchMessageByOriginalId (
    message: number,
    reply: string,
    conversation: number,
): Promise<void>
{
    await execute(
        commands.fetchMessageByOriginalId(message, reply, conversation),
        handlers.fetchMessageByOriginalId,
    );
}


/**
 * I am an API to fetch metrics.
 *
 * :param force: should fetch data anyway
 *
 * :returns: promise with nothing
 */
async function fetchMetrics (force = false): Promise<void>
{
    await execute(
        commands.fetchMetrics(force),
        handlers.fetchMetrics,
    );
}


/**
 * I am an API to fetch quality metrics.
 *
 * :returns: promise with nothing
 */
async function fetchQualityReport (): Promise<void>
{
    await execute(
        commands.fetchQualityReport(),
        handlers.fetchQualityReport,
    );
}


/**
 * I am an API to fetch real time metrics.
 *
 * :returns: promise with nothing
 */
async function fetchRealTimeMetrics (): Promise<void>
{
    await execute(
        commands.fetchRealTimeMetrics(),
        handlers.fetchRealTimeMetrics,
    );
}


/**
 * I am an API to finish a conversation.
 *
 * :param conversations: conversations ids
 *
 * :returns: promise with nothing
 */
async function finish (conversations: number[]): Promise<void>
{
    await execute(commands.finish(conversations), handlers.finish);
}


/**
 * I am an API to focus a conversation in a channel.
 *
 * :param account: account uid
 * :param contact: contact uid
 * :param conversation: conversation id
 *
 * :returns: promise with nothing
 */
async function focus (
    account: number | null,
    contact: string | number | null,
    conversation: string | number | null,
): Promise<void>
{
    await execute(
        commands.focus(account, contact, conversation),
        handlers.focus,
    );
}


/**
 * I am an API to force finish conversation.
 *
 * :param conversation: conversation id
 *
 * :returns: promise with nothing
 */
async function forceFinish (conversation: number): Promise<void>
{
    await execute(
        commands.forceFinish(conversation),
        handlers.forceFinish,
    );
}


/**
 * I am an API to load ongoing conversations.
 *
 * :param agent: agent id
 *
 * :returns: promise with nothing
 */
async function load (agent: string): Promise<void>
{
    await execute(commands.load(agent), handlers.load);
}


/**
 * I am an API to load conversations by customer.
 *
 * :param customer: customer id
 *
 * :returns: promise with nothing
 */
async function loadByCustomer (customer: number | string): Promise<void>
{
    await execute(commands.loadByCustomer(customer),
                  handlers.loadByCustomer);
}


/**
 * I am an API to load ongoing conversations by customer.
 *
 * :param customer: customer id
 *
 * :returns: promise with nothing
 */
async function loadOngoingByCustomer (customer: string | number): Promise<void>
{
    await execute(commands.loadOngoingByCustomer(customer),
                  handlers.loadOngoingByCustomer);
}


/**
 * I am an API to load conversations by period.
 *
 * :param agent: agent id
 *
 * :returns: promise with nothing
 */
async function loadByPeriod (
    agent: string,
    from: number,
    to: number,
): Promise<void>
{
    await execute(commands.loadByPeriod(agent, from, to),
                  handlers.loadByPeriod);
}


/**
 * I am an API to fetch online agents.
 *
 * :param company: company id
 *
 * :returns: promise with nothing
 */
async function loadOnlineAgents (company: number): Promise<void>
{
    // execute load online command
    await execute(commands.loadOnlineAgents(company),
                  handlers.loadOnlineAgents);
}


/**
 * I am an API to load a conversation's page.
 *
 * :returns: promise with nothing
 */
async function loadPage (): Promise<void>
{
    await execute(commands.loadPage(), handlers.loadPage);
}


/**
 * I am an API to prioritize order in ongoing and history.
 *
 * :param addHistoryBottom: ids to be added to the bottom of history
 * :param addHistoryTop: ids to be added to the top of history
 * :param addOngoingBottom: ids to be added to the bottom of ongoing
 * :param addOngoingTop: ids to be added to the top of ongoing
 * :param remove: ids to be removed
 *
 * :returns: promise with nothing
 */
async function prioritizeOrder (
    addHistoryBottom: (number | string)[],
    addHistoryTop: (number | string)[],
    addOngoingBottom: (number | string)[],
    addOngoingTop: (number | string)[],
    remove: (number | string)[],
): Promise<void>
{
    await execute(commands.prioritizeOrder(addHistoryBottom,
                                           addHistoryTop,
                                           addOngoingBottom,
                                           addOngoingTop,
                                           remove),
                  handlers.prioritizeOrder);
}


/**
 * I am an API to create conversation by conversis.
 *
 * :param account: conversation account id
 * :param channel: conversation channel
 * :param contactId: contact id
 * :param contactUid: contact uid
 * :param hasAgentAnswer: has agent answer
 * :param id: conversation id
 * :param name: contact name
 * :param lastInteraction: conversation last interaction timestamp
 * :param start: conversation start timestamp
 * :param subject: conversation subject
 * :param templateRequired: whether the template is required or not
 * :param templateSent: when template was sent or null, if not sent
 * :param unansweredMessages: conversation amount of unanswered messages
 *
 * :returns: promise with nothing
 */
async function onCreate (account: number,
                         channel: string,
                         contactId: number,
                         contactUid: string,
                         hasAgentAnswer: boolean | null,
                         id: number,
                         name: string,
                         lastInteraction: number,
                         start: number,
                         subject: string | null,
                         templateRequired: boolean,
                         templateSent: number | null,
                         unansweredMessages: number): Promise<void>
{
    // execute command
    await execute(
        commands.onCreate(account,
                          channel,
                          contactId,
                          contactUid,
                          hasAgentAnswer,
                          id,
                          name,
                          lastInteraction,
                          start,
                          subject,
                          templateRequired,
                          templateSent,
                          unansweredMessages),
        handlers.onCreate,
    );
}


/**
 * I am an API to react to an echo conversation creation.
 *
 * :param account: account id
 * :param channel: conversation channel
 * :param contactId: contact id
 * :param contactUid: contact uid
 * :param hasAgentAnswer: has agent answer
 * :param id: conversation id
 * :param name: contact name
 * :param lastInteraction: last interaction timestamp
 * :param start: start timestamp
 * :param subject: conversation subject
 * :param templateRequired: is template required
 * :param templateSent: template sent timestamp
 * :param unansweredMessages: number of unanswered messages
 *
 * :returns: nothing
 */
async function onEchoCreate (account: number,
                              channel: string,
                              contactId: number,
                              contactUid: string,
                              hasAgentAnswer: boolean | null,
                              id: number,
                              name: string,
                              lastInteraction: number,
                              start: number,
                              subject: string | null,
                              templateRequired: boolean,
                              templateSent: number | null,
                              unansweredMessages: number): Promise<void>
{
    // execute command
    await execute(
        commands.onEchoCreate(account,
                              channel,
                              contactId,
                              contactUid,
                              hasAgentAnswer,
                              id,
                              name,
                              lastInteraction,
                              start,
                              subject,
                              templateRequired,
                              templateSent,
                              unansweredMessages),
        handlers.onEchoCreate,
    );
}


/**
 * I am an api to receive an echo message.
 *
 * :param ack: message ack type
 * :param body: message body
 * :param caption: message caption
 * :param conversation: message conversation's id
 * :param id: message id
 * :param messageClass: message class
 * :param messageType: message type
 * :param name: message name
 * :param originalId: message original id
 * :param referral: message referral
 * :param reply: original id of message being replied
 * :param timestamp: message timestamp
 * :param uid: message contact's uid
 * :param unansweredMessages: number of unanswered messages or nothing
 * :param mode: message mode
 * :param outgoing: message outgoing
 *
 * :returns: nothing
 */
async function onEchoMessage (
    ack: string,
    body: string,
    caption: string,
    conversation: number,
    id: number,
    messageClass: string,
    messageType: number,
    name: string,
    originalId: string,
    referral: string | null,
    reply: string | null,
    timestamp: number,
    uid: string,
    unansweredMessages: number | null,
    mode: number | null = null,
    outgoing: string | null = null,
): Promise<void>
{
    await execute(
        commands.onEchoMessage(
            ack,
            body,
            caption,
            conversation,
            id,
            messageClass,
            messageType,
            mode,
            name,
            originalId,
            referral,
            reply,
            timestamp,
            uid,
            unansweredMessages,
            outgoing,
        ),
        handlers.onEchoMessage,
    );
}


/**
 * I am an API to react to a conversation exited notification.
 *
 * :param id: conversation id
 * :param status: conversation exit status
 * :param end: conversation end
 * :param lastInteraction: conversation last interaction timestamp
 * :param uid: conversation current app uid
 *
 * :returns: promise with nothing
 */
async function onExit (
    id: number,
    status: number,
    end: number | null,
    lastInteraction: number,
    uid: string,
): Promise<void>
{
    await execute(commands.onExit(id,
                                  status,
                                  end,
                                  lastInteraction,
                                  uid),
                  handlers.onExit);
}


/**
 * I am an API to handle a feedback.
 *
 * :param closable: error toast is closable or not
 * :param code: error code
 * :param duration: toast duration
 * :param params: error message params
 * :param title: error toast title
 * :param type: error toast type
 *
 * :returns: promise with nothing
 */
async function onFeedback (closable: boolean,
                           code: string,
                           duration: number,
                           params: IFeedbackParams,
                           title: string,
                           type: ToastTypes): Promise<void>
{
    await execute(
        commands.onFeedback(closable, code, duration, params, title, type),
        handlers.onFeedback,
    );
}


/**
 * I am an API to react to a hertbeat conflict.
 *
 * :returns: promise with nothing
 */
async function onHeartbeatConflict (): Promise<void>
{
    await execute(commands.onHeartbeatConflict(), handlers.onHeartbeatConflict);
}


/**
 * I am an API to react to a heartbeat failure.
 *
 * :returns: promise with nothing
 */
async function onHeartbeatFailure (): Promise<void>
{
    await execute(commands.onHeartbeatFailure(), handlers.onHeartbeatFailure);
}


/**
 * I am an API to react to hearbeat success.
 *
 * :returns: promise with nothing
 */
async function onHeartbeatSuccess (conversationsOnQueue: number): Promise<void>
{
    await execute(commands.onHeartbeatSuccess(conversationsOnQueue),
                  handlers.onHeartbeatSuccess);
}


/**
 * I am an api to receive a message.
 *
 * :param ack: message ack type
 * :param body: message body
 * :param caption: message caption
 * :param conversation: message conversation's id
 * :param id: message id
 * :param messageClass: message class
 * :param messageType: message type
 * :param name: message name
 * :param originalId: message original id
 * :param referral: message referral
 * :param reply: original id of message being replied
 * :param timestamp: message timestamp
 * :param uid: message contact's uid
 * :param unansweredMessages: number of unanswered messages or nothing
 * :param mode: message mode
 *
 * :returns: nothing
 */
async function onMessage (ack: string,
                          body: string,
                          caption: string,
                          conversation: number,
                          id: number,
                          messageClass: string,
                          messageType: number,
                          name: string,
                          originalId: string,
                          referral: string | null,
                          reply: string | null,
                          timestamp: number,
                          uid: string,
                          unansweredMessages: number | null,
                          mode: number | null = null): Promise<void>
{
    await execute(commands.onMessage(ack,
                                     body,
                                     caption,
                                     conversation,
                                     id,
                                     messageClass,
                                     messageType,
                                     mode,
                                     name,
                                     originalId,
                                     referral,
                                     reply,
                                     timestamp,
                                     uid,
                                     unansweredMessages),
                  handlers.onMessage);
}


/**
 * I am an API for handle type text.
 *
 * :param id: conversation id
 * :param text: typed message text
 *
 * :returns: promise with nothing
 */
async function onTypeText (
    id: number,
    text: string,
): Promise<void>
{
    await execute(
        commands.onTypeText(id, text),
        handlers.onTypeText,
    );
}


/**
 * I am an API to receive a message ack.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: message ack
 * :param originalId: message new originalId
 *
 * :returns: promise with nothing
 */
async function onMessageAck(
    conversationId: number,
    messageId: number,
    ack: AckTypes,
    originalId: string | null,
): Promise<void>
{
    await execute(commands.onMessageAck(conversationId,
                                        messageId,
                                        ack,
                                        originalId),
                  handlers.onMessageAck);
}


/**
 * I am an API for a file upload progress.
 *
 * :param id: message id
 * :param originalId: message original id
 * :param percentage: percentage of upload progress
 *
 * :returns: promise with nothing
 */
async function onUploadProgress (id: number,
                                 originalId: string,
                                 percentage: number): Promise<void>
{
    await execute(commands.onUploadProgress(id, originalId, percentage),
                  handlers.onUploadProgress);
}


/**
 * I am an API for a file upload success.
 *
 * :param id: message id
 * :param originalId: message original id
 *
 * :returns: promise with nothing
 */
async function onUploadSuccess (id: number, originalId: string): Promise<void>
{
    await execute(commands.onUploadSuccess(id, originalId),
                  handlers.onUploadSuccess);
}


/**
 * I am an API to open session on conversis.
 *
 * :returns: promise with nothing
 */
async function openSession (): Promise<void>
{
    await execute(commands.openSession(), handlers.openSession);
}


/**
 * I am an API to remove a feedback.
 *
 * :param index: feedback index
 *
 * :returns: promise with nothing
 */
async function removeFeedback (index: number): Promise<void>
{
    await execute(commands.removeFeedback(index), handlers.removeFeedback);
}


/**
 * I am an API to remove forwarding message.
 *
 * :param message: message id to remove
 *
 * :returns: promise with nothing
 */
async function removeForwardingMessage (
    message: number,
): Promise<void>
{
    await execute(
        commands.removeForwardingMessage(message),
        handlers.removeForwardingMessage,
    );
}


/**
 * I am an api to reset conversation error.
 *
 * :returns: promise with nothing
 */
async function resetError (): Promise<void>
{
    await execute(commands.resetError(), handlers.resetError);
}


/**
 * I am an api to retry message send.
 *
 * :param id: conversation id
 * :param message: message to be sent
 *
 * :returns: promise with nothing
 */
async function retryMessage (id: number, message: IMessage): Promise<void>
{
    await execute(
        commands.retryMessage(id, message, onUploadProgress, onUploadSuccess),
        handlers.retryMessage,
    );
}


/**
 * I am an api to reset conversation transfering.
 *
 * :returns: promise with nothing
 */
async function resetTransfer (): Promise<void>
{
    await execute(commands.resetTransfer(), handlers.resetTransfer);
}


/**
 * I am an API to send an email.
 *
 * :param account: account id
 * :param attachments: email attachments
 * :param body: email body
 * :param contact: contact uid
 * :param conversation: conversation id or nothing
 * :param sender: user that sent the message
 * :param subject: conversation subject
 *
 * :returns: promise with nothing
 */
async function sendEmail (
    account: number,
    attachments: File[],
    body: string,
    contact: string,
    conversation: number | null,
    sender: string,
    subject: string,
): Promise<void>
{
    await execute(
        commands.sendEmail(account,
                           attachments,
                           body,
                           contact,
                           conversation,
                           onUploadProgress,
                           onUploadSuccess,
                           sender,
                           subject),
        handlers.sendEmail,
    );
}


/**
 * I am an api to send a message.
 *
 * :param contact: contact uid
 * :param conversation: conversation id
 * :param message: message to be sent
 * :param uploaded: payload upload status
 *
 * :returns: promise with nothing
 */
async function sendMessage (
    contact: string,
    conversation: number,
    message: IMessage,
    uploaded = false,
): Promise<void>
{
    await execute(
        commands.sendMessage(contact,
                             conversation,
                             message,
                             uploaded,
                             onUploadProgress,
                             onUploadSuccess),
        handlers.sendMessage,
    );
}


/**
 * I am an api to send a template.
 *
 * :param conversation: conversation id
 * :param templateInput: template input data for conversis
 *
 * :returns: promise with nothing
 */
async function sendTemplate(
    conversation: number,
    templateInput: ITemplateInput,
): Promise<void>
{
    await execute(
        commands.sendTemplate(conversation, templateInput),
        handlers.sendTemplate,
    );
}


/**
 * I am an API to set metrics period.
 *
 * :param endDate: final timestamp
 * :param startDate: initial timestamp
 * :param filters: metrics filters
 *
 * :returns: promise with nothing
 */
async function setMetricsPeriod (
    endDate: number,
    startDate: number,
    filters?: IMetricsFilters,
): Promise<void>
{
    await execute(
        commands.setMetricsPeriod(endDate, startDate, filters),
        handlers.setMetricsPeriod,
    );
}


/**
 * I am an API to dispatch the quality analysis of the loaded conversations.
 *
 * :returns: promise with nothing
 */
async function analyzeQuality (): Promise<void>
{
    await execute(
        commands.analyzeQuality(),
        handlers.analyzeQuality,
    );
}


/**
 * I am an API to expire dispatched analyses that got no event in time.
 *
 * :param conversations: dispatched conversation ids
 *
 * :returns: promise with nothing
 */
async function expireQualityAnalyses (conversations: number[]): Promise<void>
{
    await execute(
        commands.expireQualityAnalyses(conversations),
        handlers.expireQualityAnalyses,
    );
}


/**
 * I am an API to handle a conversation-analyzed event.
 *
 * :param analyzedAt: analysis timestamp
 * :param conversation: conversation id
 * :param failed: analysis failed
 * :param score: score, null when failed
 *
 * :returns: promise with nothing
 */
async function onQualityAnalyzed (
    analyzedAt: number,
    conversation: number,
    failed: boolean,
    score: number | null,
): Promise<void>
{
    await execute(
        commands.onQualityAnalyzed(analyzedAt, conversation, failed, score),
        handlers.onQualityAnalyzed,
    );
}


/**
 * I am an API to leave the quality report screen.
 *
 * :returns: promise with nothing
 */
async function stopQualityReport (): Promise<void>
{
    await execute(
        commands.stopQualityReport(),
        handlers.stopQualityReport,
    );
}


/**
 * I am an API to set quality report metadata.
 *
 * :param metadata: quality report metadata
 *
 * :returns: promise with nothing
 */
async function setQualityMetadata (metadata: IQualityMetadata): Promise<void>
{
    await execute(
        commands.setQualityMetadata(metadata),
        handlers.setQualityMetadata,
    );
}


/**
 * I am an API to start conversations.
 *
 * :param isAdmin: whether the user is an administrator
 *
 * :returns: promise with nothing
 */
async function start (isAdmin: boolean): Promise<void>
{
    await execute(commands.start(isAdmin), handlers.start);
}


/**
 * I am an API to start forward message.
 *
 * :returns: promise with nothing
 */
async function startForward (): Promise<void>
{
    await execute(
        commands.startForward(),
        handlers.startForward,
    );
}


/**
 * I am an API to start conversations.
 *
 * :param isAdmin: whether the user is an administrator
 *
 * :returns: promise with nothing
 */
async function stop (isAdmin: boolean): Promise<void>
{
    await execute(commands.stop(isAdmin), handlers.stop);
}


/**
 * I am an API to stop forward message.
 *
 * :returns: promise with nothing
 */
async function stopForward (): Promise<void>
{
    await execute(
        commands.stopForward(),
        handlers.stopForward,
    );
}


/**
 * EXPORTS
 */
export {
    addForwardingMessage,
    analyzeQuality,
    expireQualityAnalyses,
    onQualityAnalyzed,
    remove,
    closeSession,
    create,
    ensure,
    ensureOngoing,
    fetch,
    fetchAgentsMessagesMetrics,
    fetchByIds,
    fetchMessageByOriginalId,
    fetchMetrics,
    fetchQualityReport,
    fetchRealTimeMetrics,
    finish,
    focus,
    forceFinish,
    listen,
    load,
    loadByCustomer,
    loadByPeriod,
    loadOngoingByCustomer,
    loadOnlineAgents,
    loadPage,
    prioritizeOrder,
    onCreate,
    onEchoCreate,
    onEchoMessage,
    onExit,
    onFeedback,
    onHeartbeatConflict,
    onHeartbeatFailure,
    onHeartbeatSuccess,
    onMessage,
    onTypeText,
    onMessageAck,
    openSession,
    removeFeedback,
    removeForwardingMessage,
    resetError,
    resetTransfer,
    retryMessage,
    sendEmail,
    sendMessage,
    sendTemplate,
    setMetricsPeriod,
    setQualityMetadata,
    start,
    startForward,
    stop,
    stopForward,
    stopQualityReport,
    transfer,
};
